import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Store, BookOpen, QrCode, LogOut, Users, Building2, CreditCard, UserCircle2 } from "lucide-react";
import { RestaurantSwitcher } from "./RestaurantSwitcher";
import { TrialBanner } from "./TrialBanner";

export function DashboardLayout({ children, role = "owner" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const ownerNav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/dashboard/menu", label: "Menu", icon: BookOpen },
    { to: "/dashboard/restaurants", label: "My Outlets", icon: Building2 },
    { to: "/dashboard/profile", label: "Outlet Branding", icon: Store },
    { to: "/dashboard/qr", label: "QR Code", icon: QrCode },
    { to: "/dashboard/billing", label: "Billing", icon: CreditCard },
    { to: "/dashboard/me", label: "My Profile", icon: UserCircle2 },
  ];
  const adminNav = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/restaurants", label: "Restaurants", icon: Building2 },
    { to: "/dashboard/me", label: "My Profile", icon: UserCircle2 },
  ];
  const nav = role === "super_admin" ? adminNav : ownerNav;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex" data-testid="dashboard-layout">
      {/* Sticky Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border sticky top-0 h-screen shrink-0" data-testid="sidebar">
        <div className="px-6 py-5 border-b border-border">
          <Logo size={56} />
        </div>
        {role === "owner" && (
          <div className="p-3 border-b border-border">
            <RestaurantSwitcher />
          </div>
        )}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/dashboard" || n.to === "/admin"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-brand-navy text-white" : "text-foreground hover:bg-muted"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Account</div>
          <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate mb-3">{user?.email}</div>
          <Button variant="outline" size="sm" className="w-full" onClick={doLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {role === "owner" && <TrialBanner />}
        <header className="lg:hidden bg-white border-b border-border px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo size={44} />
          <div className="flex items-center gap-1">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} className="p-2 rounded-md hover:bg-muted" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                <n.icon className="w-5 h-5" />
              </Link>
            ))}
            <button onClick={doLogout} className="p-2 rounded-md hover:bg-muted" data-testid="mobile-logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
        {role === "owner" && (
          <div className="lg:hidden p-3 bg-white border-b border-border">
            <RestaurantSwitcher />
          </div>
        )}
        <main className="flex-1 p-6 lg:p-10 max-w-[1400px] w-full mx-auto" data-testid="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
}
